import { use_creation_default } from './chunk-GP2PA3QV.mjs';
import { use_update_default } from './chunk-LZHGILJJ.mjs';
import { useMountedState } from './chunk-IES6KWJT.mjs';
import { use_event_listener_default, getTargetElement } from './chunk-3HBUA6YI.mjs';
import { use_mount_default } from './chunk-XS6BHIMP.mjs';
import { use_update_effect_default } from './chunk-WUKIS7AT.mjs';
import { is_browser_default } from './chunk-DOLOJKHH.mjs';
import { use_unmount_default } from './chunk-EKEPE4ON.mjs';
import { use_memoized_fn_default, use_latest_default, is_dev_default, isFunction } from './chunk-24OWA2BQ.mjs';
import { useRef, useState, useCallback, useEffect, useReducer, useMemo } from 'react';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

function useAsyncFn(fn, deps = [], initialState = { loading: false }) {
  const lastCallId = useRef(0);
  const isMounted = useMountedState();
  const [state, set] = useState(initialState);
  const callback = useCallback((...args) => {
    lastCallId.current += 1;
    const callId = lastCallId.current;
    if (!state.loading) {
      set((prevState) => ({ ...prevState, loading: true }));
    }
    return fn(...args).then(
      (value) => {
        if (isMounted() && callId === lastCallId.current) {
          set({ value, loading: false });
        }
        return value;
      },
      (error) => {
        if (isMounted() && callId === lastCallId.current) {
          set({ error, loading: false });
        }
        return error;
      }
    );
  }, deps);
  return [state, callback];
}
function useAsync(fn, deps = []) {
  const [state, callback] = useAsyncFn(fn, deps, {
    loading: true
  });
  useEffect(() => {
    void callback();
  }, [callback]);
  return state;
}
function useContinuousRetry(callback, interval = 100, options = {}) {
  const [hasResolved, setHasResolved] = useState(false);
  const { maxRetries = Number.POSITIVE_INFINITY } = options;
  useEffect(() => {
    let retries = 0;
    const id = window.setInterval(() => {
      if (callback()) {
        setHasResolved(true);
        window.clearInterval(id);
      } else if (retries >= maxRetries) {
        window.clearInterval(id);
      } else {
        retries += 1;
      }
    }, interval);
    return () => {
      window.clearInterval(id);
    };
  }, [callback, interval, maxRetries]);
  return hasResolved;
}
function useFetch(url, options) {
  const cache2 = useRef({});
  const cancelRequest = useRef(false);
  const initialState = {
    data: void 0,
    error: void 0
  };
  const fetchReducer = (state2, action) => {
    switch (action.type) {
      case "loading": {
        return { ...initialState };
      }
      case "fetched": {
        return { ...initialState, data: action.payload };
      }
      case "error": {
        return { ...initialState, error: action.payload };
      }
      default: {
        return state2;
      }
    }
  };
  const [state, dispatch] = useReducer(fetchReducer, initialState);
  useEffect(() => {
    if (!url) {
      return;
    }
    cancelRequest.current = false;
    const fetchData = async () => {
      dispatch({ type: "loading" });
      if (cache2.current[url]) {
        dispatch({ type: "fetched", payload: cache2.current[url] });
        return;
      }
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const data = await response.json();
        cache2.current[url] = data;
        if (cancelRequest.current) {
          return;
        }
        dispatch({ type: "fetched", payload: data });
      } catch (error) {
        if (cancelRequest.current) {
          return;
        }
        dispatch({ type: "error", payload: error });
      }
    };
    void fetchData();
    return () => {
      cancelRequest.current = true;
    };
  }, [url]);
  return state;
}

// src/async/useRequest/src/utils/cache.ts
var cache = /* @__PURE__ */ new Map();
var clearTimer = (cachedData) => {
  if (cachedData?.timer) {
    clearTimeout(cachedData.timer);
  }
};
var setCache = (key, cacheTime, cachedData) => {
  const currentCache = cache.get(key);
  clearTimer(currentCache);
  let timer;
  if (cacheTime > -1) {
    timer = setTimeout(() => {
      cache.delete(key);
    }, cacheTime);
  }
  cache.set(key, {
    ...cachedData,
    timer
  });
};
var getCache = (key) => cache.get(key);
var clearCache = (key) => {
  if (key) {
    const cacheKeys = Array.isArray(key) ? key : [key];
    for (const cacheKey of cacheKeys) {
      const currentCache = cache.get(cacheKey);
      clearTimer(currentCache);
      cache.delete(cacheKey);
    }
  } else {
    for (const currentCache of cache.values()) {
      clearTimer(currentCache);
    }
    cache.clear();
  }
};
var useAutoRunPlugin = (fetchInstance, {
  manual,
  ready = true,
  defaultParams = [],
  refreshDeps = [],
  refreshDepsAction
}) => {
  const hasAutoRun = useRef(false);
  hasAutoRun.current = false;
  use_update_effect_default(() => {
    if (!manual && ready) {
      hasAutoRun.current = true;
      fetchInstance.run(...defaultParams);
    }
  }, [ready]);
  use_update_effect_default(() => {
    if (hasAutoRun.current) {
      return;
    }
    if (!manual) {
      hasAutoRun.current = true;
      if (refreshDepsAction) {
        refreshDepsAction();
      } else {
        fetchInstance.refresh();
      }
    }
  }, [...refreshDeps]);
  return {
    onBefore: () => {
      if (!ready) {
        return {
          stopNow: true
        };
      }
    }
  };
};
useAutoRunPlugin.onInit = ({ ready = true, manual }) => ({
  loading: !manual && ready
});
var use_auto_run_plugin_default = useAutoRunPlugin;

// src/async/useRequest/src/utils/cache-promise.ts
var cachePromise = /* @__PURE__ */ new Map();
var getCachePromise = (cacheKey) => cachePromise.get(cacheKey);
var setCachePromise = (cacheKey, promise) => {
  cachePromise.set(cacheKey, promise);
  promise.then((res) => {
    cachePromise.delete(cacheKey);
    return res;
  }).catch(() => {
    cachePromise.delete(cacheKey);
  });
};

// src/async/useRequest/src/utils/cache-subscribe.ts
var listeners = {};
var trigger = (key, data) => {
  if (listeners[key]) {
    for (const item of listeners[key]) {
      item(data);
    }
  }
};
var subscribe = (key, listener) => {
  if (!listeners[key]) {
    listeners[key] = [];
  }
  listeners[key].push(listener);
  return function unsubscribe() {
    const index = listeners[key].indexOf(listener);
    listeners[key].splice(index, 1);
  };
};

// src/async/useRequest/src/plugins/use-cache-plugin.ts
var useCachePlugin = (fetchInstance, {
  cacheKey,
  cacheTime = 5 * 60 * 1e3,
  staleTime = 0,
  setCache: customSetCache,
  getCache: customGetCache
}) => {
  const unSubscribeRef = useRef(void 0);
  const currentPromiseRef = useRef(void 0);
  const _setCache = (key, cachedData) => {
    if (customSetCache) {
      customSetCache(cachedData);
    } else {
      setCache(key, cacheTime, cachedData);
    }
    trigger(key, cachedData.data);
  };
  const _getCache = (key, params = []) => {
    if (customGetCache) {
      return customGetCache(params);
    }
    return getCache(key);
  };
  use_creation_default(() => {
    if (!cacheKey) {
      return;
    }
    const cacheData = _getCache(cacheKey);
    if (cacheData && Object.hasOwn(cacheData, "data")) {
      fetchInstance.state.data = cacheData.data;
      fetchInstance.state.params = cacheData.params;
      if (staleTime === -1 || Date.now() - cacheData.time <= staleTime) {
        fetchInstance.state.loading = false;
      }
    }
    unSubscribeRef.current = subscribe(cacheKey, (data) => {
      fetchInstance.setState({ data });
    });
  }, []);
  use_unmount_default(() => {
    unSubscribeRef.current?.();
  });
  if (!cacheKey) {
    return {};
  }
  return {
    onBefore: (params) => {
      const cacheData = _getCache(cacheKey, params);
      if (!(cacheData && Object.hasOwn(cacheData, "data"))) {
        return {};
      }
      if (staleTime === -1 || Date.now() - cacheData.time <= staleTime) {
        return {
          loading: false,
          data: cacheData?.data,
          error: void 0,
          returnNow: true
        };
      }
      return {
        data: cacheData?.data,
        error: void 0
      };
    },
    onRequest: (service, args) => {
      let servicePromise = getCachePromise(cacheKey);
      if (servicePromise && servicePromise !== currentPromiseRef.current) {
        return { servicePromise };
      }
      servicePromise = service(...args);
      currentPromiseRef.current = servicePromise;
      setCachePromise(cacheKey, servicePromise);
      return { servicePromise };
    },
    onSuccess: (data, params) => {
      if (cacheKey) {
        unSubscribeRef.current?.();
        _setCache(cacheKey, {
          data,
          params,
          time: Date.now()
        });
        unSubscribeRef.current = subscribe(cacheKey, (d) => {
          fetchInstance.setState({ data: d });
        });
      }
    },
    onMutate: (data) => {
      if (cacheKey) {
        unSubscribeRef.current?.();
        _setCache(cacheKey, {
          data,
          params: fetchInstance.state.params,
          time: Date.now()
        });
        unSubscribeRef.current = subscribe(cacheKey, (d) => {
          fetchInstance.setState({ data: d });
        });
      }
    }
  };
};
var use_cache_plugin_default = useCachePlugin;
var useDebouncePlugin = (fetchInstance, { debounceWait, debounceLeading, debounceTrailing, debounceMaxWait }) => {
  const debouncedRef = useRef(void 0);
  const options = useMemo(() => {
    const ret = {};
    if (debounceLeading !== void 0) {
      ret.leading = debounceLeading;
    }
    if (debounceTrailing !== void 0) {
      ret.trailing = debounceTrailing;
    }
    if (debounceMaxWait !== void 0) {
      ret.maxWait = debounceMaxWait;
    }
    return ret;
  }, [debounceLeading, debounceTrailing, debounceMaxWait]);
  useEffect(() => {
    if (debounceWait) {
      const _originRunAsync = fetchInstance.runAsync.bind(fetchInstance);
      debouncedRef.current = debounce(
        (callback) => {
          callback();
        },
        debounceWait,
        options
      );
      fetchInstance.runAsync = async (...args) => await new Promise((resolve, reject) => {
        debouncedRef.current?.(() => {
          _originRunAsync(...args).then(resolve).catch(reject);
        });
      });
      return () => {
        debouncedRef.current?.cancel();
        fetchInstance.runAsync = _originRunAsync;
      };
    }
  }, [debounceWait, options]);
  if (!debounceWait) {
    return {};
  }
  return {
    onCancel: () => {
      debouncedRef.current?.cancel();
    }
  };
};
var use_debounce_plugin_default = useDebouncePlugin;
var useLoadingDelayPlugin = (fetchInstance, { loadingDelay, ready }) => {
  const timerRef = useRef(void 0);
  if (!loadingDelay) {
    return {};
  }
  const cancelTimeout = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };
  return {
    onBefore: () => {
      cancelTimeout();
      if (ready !== false) {
        timerRef.current = setTimeout(() => {
          fetchInstance.setState({
            loading: true
          });
        }, loadingDelay);
      }
      return {
        loading: false
      };
    },
    onFinally: () => {
      cancelTimeout();
    },
    onCancel: () => {
      cancelTimeout();
    }
  };
};
var use_loading_delay_plugin_default = useLoadingDelayPlugin;

// src/async/useRequest/src/utils/is-document-visible.ts
function isDocumentVisible() {
  if (is_browser_default) {
    return document.visibilityState !== "hidden";
  }
  return true;
}

// src/async/useRequest/src/utils/subscribe-re-visible.ts
var listeners2 = /* @__PURE__ */ new Set();
var subscribe2 = (listener) => {
  listeners2.add(listener);
  return function unsubscribe() {
    if (listeners2.has(listener)) {
      listeners2.delete(listener);
    }
  };
};
if (is_browser_default) {
  const revalidate = () => {
    if (!isDocumentVisible()) {
      return;
    }
    for (const listener of listeners2) {
      listener();
    }
  };
  window.addEventListener("visibilitychange", revalidate, false);
}
var subscribe_re_visible_default = subscribe2;

// src/async/useRequest/src/plugins/use-polling-plugin.ts
var usePollingPlugin = (fetchInstance, { pollingInterval, pollingWhenHidden = true, pollingErrorRetryCount = -1 }) => {
  const timerRef = useRef(void 0);
  const unsubscribeRef = useRef(void 0);
  const countRef = useRef(0);
  const stopPolling = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    unsubscribeRef.current?.();
  };
  use_update_effect_default(() => {
    if (!pollingInterval) {
      stopPolling();
    }
  }, [pollingInterval]);
  if (!pollingInterval) {
    return {};
  }
  return {
    onBefore: () => {
      stopPolling();
    },
    onError: () => {
      countRef.current += 1;
    },
    onSuccess: () => {
      countRef.current = 0;
    },
    onFinally: () => {
      if (pollingErrorRetryCount === -1 || // When an error occurs, the request is not repeated after pollingErrorRetryCount retries
      pollingErrorRetryCount !== -1 && countRef.current <= pollingErrorRetryCount) {
        timerRef.current = setTimeout(() => {
          if (pollingWhenHidden || isDocumentVisible()) {
            fetchInstance.refresh();
          } else {
            unsubscribeRef.current = subscribe_re_visible_default(() => {
              fetchInstance.refresh();
            });
          }
        }, pollingInterval);
      } else {
        countRef.current = 0;
      }
    },
    onCancel: () => {
      stopPolling();
    }
  };
};
var use_polling_plugin_default = usePollingPlugin;

// src/async/useRequest/src/utils/limit.ts
function limit(fn, timespan) {
  let pending = false;
  return (...args) => {
    if (pending) {
      return;
    }
    pending = true;
    fn(...args);
    setTimeout(() => {
      pending = false;
    }, timespan);
  };
}

// src/async/useRequest/src/utils/is-online.ts
var isOnline = () => {
  if (is_browser_default && navigator.onLine !== void 0) {
    return navigator.onLine;
  }
  return true;
};
var is_online_default = isOnline;

// src/async/useRequest/src/utils/subscribe-focus.ts
var listeners3 = /* @__PURE__ */ new Set();
var subscribe3 = (listener) => {
  listeners3.add(listener);
  return function unsubscribe() {
    if (listeners3.has(listener)) {
      listeners3.delete(listener);
    }
  };
};
if (is_browser_default) {
  const revalidate = () => {
    if (!(isDocumentVisible() && is_online_default())) {
      return;
    }
    for (const listener of listeners3) {
      listener();
    }
  };
  window.addEventListener("visibilitychange", revalidate, false);
  window.addEventListener("focus", revalidate, false);
}
var subscribe_focus_default = subscribe3;

// src/async/useRequest/src/plugins/use-refresh-on-window-focus-plugin.ts
var useRefreshOnWindowFocusPlugin = (fetchInstance, { refreshOnWindowFocus, focusTimespan = 5e3 }) => {
  const unsubscribeRef = useRef(void 0);
  const stopSubscribe = () => {
    unsubscribeRef.current?.();
  };
  useEffect(() => {
    if (refreshOnWindowFocus) {
      const limitRefresh = limit(
        fetchInstance.refresh.bind(fetchInstance),
        focusTimespan
      );
      unsubscribeRef.current = subscribe_focus_default(() => {
        limitRefresh();
      });
    }
    return () => {
      stopSubscribe();
    };
  }, [refreshOnWindowFocus, focusTimespan]);
  use_unmount_default(() => {
    stopSubscribe();
  });
  return {};
};
var use_refresh_on_window_focus_plugin_default = useRefreshOnWindowFocusPlugin;
var useRetryPlugin = (fetchInstance, { retryInterval, retryCount }) => {
  const timerRef = useRef(void 0);
  const countRef = useRef(0);
  const triggerByRetry = useRef(false);
  if (!retryCount) {
    return {};
  }
  return {
    onBefore: () => {
      if (!triggerByRetry.current) {
        countRef.current = 0;
      }
      triggerByRetry.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    onSuccess: () => {
      countRef.current = 0;
    },
    onError: () => {
      countRef.current += 1;
      if (retryCount === -1 || countRef.current <= retryCount) {
        const timeout = retryInterval ?? Math.min(1e3 * 2 ** countRef.current, 3e4);
        timerRef.current = setTimeout(() => {
          triggerByRetry.current = true;
          fetchInstance.refresh();
        }, timeout);
      } else {
        countRef.current = 0;
      }
    },
    onCancel: () => {
      countRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }
  };
};
var use_retry_plugin_default = useRetryPlugin;
var useThrottlePlugin = (fetchInstance, { throttleWait, throttleLeading, throttleTrailing }) => {
  const throttledRef = useRef(void 0);
  const options = {};
  if (throttleLeading !== void 0) {
    options.leading = throttleLeading;
  }
  if (throttleTrailing !== void 0) {
    options.trailing = throttleTrailing;
  }
  useEffect(() => {
    if (throttleWait) {
      const _originRunAsync = fetchInstance.runAsync.bind(fetchInstance);
      throttledRef.current = throttle(
        (callback) => {
          callback();
        },
        throttleWait,
        options
      );
      fetchInstance.runAsync = async (...args) => await new Promise((resolve, reject) => {
        throttledRef.current?.(() => {
          _originRunAsync(...args).then(resolve).catch(reject);
        });
      });
      return () => {
        fetchInstance.runAsync = _originRunAsync;
        throttledRef.current?.cancel();
      };
    }
  }, [throttleWait, throttleLeading, throttleTrailing]);
  if (!throttleWait) {
    return {};
  }
  return {
    onCancel: () => {
      throttledRef.current?.cancel();
    }
  };
};
var use_throttle_plugin_default = useThrottlePlugin;

// src/async/useRequest/src/fetch.ts
var Fetch = class {
  /**
   * Creates a Fetch instance. Loading starts true unless the request is manual.
   *
   * @param serviceRef - Ref to the current service function.
   * @param options - Request options, including manual and the lifecycle callbacks.
   * @param subscribe - Called after each state change so React can re-render.
   * @param initState - Optional overrides for the initial state.
   */
  constructor(serviceRef, options, subscribe4, initState = {}) {
    this.serviceRef = serviceRef;
    this.options = options;
    this.subscribe = subscribe4;
    this.initState = initState;
    this.state = {
      ...this.state,
      loading: !options.manual,
      ...initState
    };
  }
  serviceRef;
  options;
  subscribe;
  initState;
  pluginImpls = [];
  count = 0;
  state = {
    loading: false,
    params: void 0,
    data: void 0,
    error: void 0
  };
  /**
   * Merges a partial state update into the current state and notifies subscribers.
   *
   * @param s - Fields to merge into the current state.
   */
  setState(s = {}) {
    this.state = {
      ...this.state,
      ...s
    };
    this.subscribe();
  }
  /**
   * Calls the named handler on every plugin and merges the objects they return.
   *
   * @param event - Name of the plugin handler to call.
   * @param rest - Arguments passed to each handler.
   * @returns The merged objects returned by the handlers.
   */
  runPluginHandler(event, ...rest) {
    const r = this.pluginImpls.map((i) => i[event]?.(...rest)).filter(Boolean);
    return r.reduce((acc, cur) => ({ ...acc, ...cur }), {});
  }
  /**
   * Runs the service with the given params. Plugins can stop the request or return early. A call that is superseded by a newer call resolves without updating state.
   *
   * @param params - Arguments passed to the service.
   * @returns A promise of the service result.
   * @throws {@link Error} When the service rejects and this call is still the latest.
   */
  async runAsync(...params) {
    this.count += 1;
    const currentCount = this.count;
    const {
      stopNow = false,
      returnNow = false,
      ...state
    } = this.runPluginHandler("onBefore", params);
    if (stopNow) {
      return state.data;
    }
    this.setState({
      loading: true,
      params,
      ...state
    });
    if (returnNow) {
      return state.data;
    }
    this.options.onBefore?.(params);
    try {
      let { servicePromise } = this.runPluginHandler(
        "onRequest",
        this.serviceRef.current,
        params
      );
      if (!servicePromise) {
        servicePromise = this.serviceRef.current(...params);
      }
      const res = await servicePromise;
      if (currentCount !== this.count) {
        return new Promise(() => {
        });
      }
      this.setState({
        data: res,
        error: void 0,
        loading: false
      });
      this.options.onSuccess?.(res, params);
      this.runPluginHandler("onSuccess", res, params);
      this.options.onFinally?.(params, res);
      if (currentCount === this.count) {
        this.runPluginHandler("onFinally", params, res);
      }
      return res;
    } catch (error) {
      if (currentCount !== this.count) {
        return await new Promise(() => {
        });
      }
      this.setState({
        // SAFETY: the caught rejection is unknown; the request state stores it as Error or undefined.
        error,
        loading: false
      });
      this.options.onError?.(error, params);
      this.runPluginHandler("onError", error, params);
      this.options.onFinally?.(params, void 0, error);
      if (currentCount === this.count) {
        this.runPluginHandler("onFinally", params, void 0, error);
      }
      throw error;
    }
  }
  /**
   * Starts a request without returning its promise. Errors go to onError, or are logged when none is set.
   *
   * @param params - Arguments passed to the service.
   */
  run(...params) {
    this.runAsync(...params).catch((error) => {
      if (!this.options.onError) {
        console.error(error);
      }
    });
  }
  /**
   * Cancels the in-flight request. Its result is ignored and loading is cleared.
   */
  cancel() {
    this.count += 1;
    this.setState({
      loading: false
    });
    this.runPluginHandler("onCancel");
  }
  /**
   * Re-runs the request with the params from the most recent call.
   */
  refresh() {
    this.run(...this.state.params ?? []);
  }
  /**
   * Re-runs the request with the params from the most recent call and returns its promise.
   *
   * @returns A promise of the service result.
   */
  async refreshAsync() {
    return await this.runAsync(...this.state.params ?? []);
  }
  /**
   * Sets the data directly, without calling the service.
   *
   * @param data - The new data, or a function that receives the current data and returns the next.
   */
  mutate(data) {
    const targetData = isFunction(data) ? data(this.state.data) : data;
    this.runPluginHandler("onMutate", targetData);
    this.setState({
      data: targetData
    });
  }
};

// src/async/useRequest/src/use-request-implement.ts
var useRequestImplement = (service, options = {}, plugins = []) => {
  const { manual = false, ready = true, ...rest } = options;
  if (is_dev_default && options.defaultParams && !Array.isArray(options.defaultParams)) {
    console.warn(
      `expected defaultParams is array, got ${typeof options.defaultParams}`
    );
  }
  const fetchOptions = {
    manual,
    ready,
    ...rest
  };
  const serviceRef = use_latest_default(service);
  const update = use_update_default();
  const fetchInstance = use_creation_default(() => {
    const initState = plugins.map((p) => p?.onInit?.(fetchOptions)).filter(Boolean);
    return new Fetch(
      serviceRef,
      fetchOptions,
      update,
      initState.reduce(
        (acc, cur) => ({ ...acc, ...cur }),
        {}
      )
    );
  }, []);
  fetchInstance.options = fetchOptions;
  fetchInstance.pluginImpls = plugins.map(
    (p) => p(fetchInstance, fetchOptions)
  );
  use_mount_default(() => {
    if (!manual && ready) {
      const params = fetchInstance.state.params ?? options.defaultParams ?? [];
      fetchInstance.run(...params);
    }
  });
  use_unmount_default(() => {
    fetchInstance.cancel();
  });
  return {
    loading: fetchInstance.state.loading,
    data: fetchInstance.state.data,
    error: fetchInstance.state.error,
    params: fetchInstance.state.params ?? [],
    cancel: use_memoized_fn_default(fetchInstance.cancel.bind(fetchInstance)),
    refresh: use_memoized_fn_default(fetchInstance.refresh.bind(fetchInstance)),
    refreshAsync: use_memoized_fn_default(fetchInstance.refreshAsync.bind(fetchInstance)),
    run: use_memoized_fn_default(fetchInstance.run.bind(fetchInstance)),
    runAsync: use_memoized_fn_default(fetchInstance.runAsync.bind(fetchInstance)),
    mutate: use_memoized_fn_default(fetchInstance.mutate.bind(fetchInstance))
  };
};
var use_request_implement_default = useRequestImplement;

// src/async/useRequest/src/use-request.ts
function useRequest(service, options, plugins) {
  return use_request_implement_default(service, options, [
    ...plugins ?? [],
    use_debounce_plugin_default,
    use_loading_delay_plugin_default,
    use_polling_plugin_default,
    use_refresh_on_window_focus_plugin_default,
    use_throttle_plugin_default,
    use_auto_run_plugin_default,
    use_cache_plugin_default,
    use_retry_plugin_default
  ]);
}
var use_request_default = useRequest;

// src/async/useRequest/index.ts
var useRequest_default = use_request_default;

// src/utils/rect.ts
var getScrollTop = (el) => {
  if (el === document || el === document.documentElement || el === document.body) {
    return Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    );
  }
  return el.scrollTop;
};
var getScrollHeight = (el) => (
  // SAFETY: el is a Document or Element; scroll height is read from the Element side of that union.
  el.scrollHeight || Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
);
var getClientHeight = (el) => (
  // SAFETY: el is a Document or Element; client height is read from the Element side of that union.
  el.clientHeight || Math.max(document.documentElement.clientHeight, document.body.clientHeight)
);

// src/async/use-infinite-scroll.tsx
var useInfiniteScroll = (service, options = {}) => {
  const {
    target,
    isNoMore,
    threshold = 100,
    direction = "bottom",
    reloadDeps = [],
    manual,
    onBefore,
    onSuccess,
    onError,
    onFinally
  } = options;
  const [finalData, setFinalData] = useState();
  const [loadingMore, setLoadingMore] = useState(false);
  const isScrollToTop = direction === "top";
  const lastScrollTop = useRef(void 0);
  const scrollBottom = useRef(0);
  const noMore = useMemo(() => {
    if (!isNoMore) {
      return false;
    }
    return isNoMore(finalData);
  }, [finalData]);
  const { loading, error, run, runAsync, cancel } = useRequest_default(
    async (lastData) => {
      const currentData = await service(lastData);
      return { currentData, lastData };
    },
    {
      manual,
      onFinally: (_, d, e) => {
        setLoadingMore(false);
        onFinally?.(d?.currentData, e);
      },
      onBefore: () => onBefore?.(),
      onSuccess: (d) => {
        if (d.lastData) {
          setFinalData({
            ...d.currentData,
            list: isScrollToTop ? [...d.currentData.list, ...d.lastData.list ?? []] : [...d.lastData.list ?? [], ...d.currentData.list]
          });
        } else {
          setFinalData({
            ...d.currentData,
            list: [...d.currentData.list ?? []]
          });
        }
        setTimeout(() => {
          requestAnimationFrame(() => {
            if (isScrollToTop) {
              let el = getTargetElement(target);
              el = el === document ? document.documentElement : el;
              if (el) {
                const scrollHeight = getScrollHeight(el);
                el.scrollTo(
                  0,
                  scrollHeight - scrollBottom.current
                );
              }
            } else {
              scrollMethod();
            }
          });
        }, 0);
        onSuccess?.(d.currentData);
      },
      onError: (e) => onError?.(e)
    }
  );
  const loadMore = use_memoized_fn_default(() => {
    if (noMore) {
      return;
    }
    setLoadingMore(true);
    run(finalData);
  });
  const runAsyncForCurrent = async (data) => {
    const res = await runAsync(data);
    return res.currentData;
  };
  const loadMoreAsync = use_memoized_fn_default(async () => {
    if (noMore) {
      throw void 0;
    }
    setLoadingMore(true);
    return await runAsyncForCurrent(finalData);
  });
  const reload = () => {
    setLoadingMore(false);
    run();
  };
  const reloadAsync = async () => {
    setLoadingMore(false);
    return await runAsyncForCurrent();
  };
  const scrollMethod = () => {
    const el = getTargetElement(target);
    if (!el) {
      return;
    }
    const targetEl = el === document ? document.documentElement : el;
    const scrollTop = getScrollTop(targetEl);
    const scrollHeight = getScrollHeight(targetEl);
    const clientHeight = getClientHeight(targetEl);
    if (isScrollToTop) {
      if (lastScrollTop.current !== void 0 && lastScrollTop.current > scrollTop && scrollTop <= threshold) {
        loadMore();
      }
      lastScrollTop.current = scrollTop;
      scrollBottom.current = scrollHeight - scrollTop;
    } else if (scrollHeight - scrollTop <= clientHeight + threshold) {
      loadMore();
    }
  };
  use_event_listener_default(
    "scroll",
    () => {
      if (loading || loadingMore) {
        return;
      }
      scrollMethod();
    },
    { target }
  );
  use_update_effect_default(() => {
    run();
  }, [...reloadDeps]);
  return {
    data: finalData,
    loading: !loadingMore && loading,
    error,
    loadingMore,
    noMore,
    loadMore,
    loadMoreAsync,
    reload: use_memoized_fn_default(reload),
    reloadAsync: use_memoized_fn_default(reloadAsync),
    mutate: setFinalData,
    cancel
  };
};
var use_infinite_scroll_default = useInfiniteScroll;
var useLockFn = (fn) => {
  const lockRef = useRef(false);
  return useCallback(
    async (...args) => {
      if (lockRef.current) {
        return;
      }
      lockRef.current = true;
      try {
        return await fn(...args);
      } finally {
        lockRef.current = false;
      }
    },
    [fn]
  );
};
var use_lock_fn_default = useLockFn;
var usePagination = (service, options = {}) => {
  const { defaultPageSize = 10, defaultCurrent = 1, ...rest } = options;
  const result = useRequest_default(service, {
    // SAFETY: the first request param is the pagination object; the cast gives the param list the generic TParams type.
    defaultParams: [
      { current: defaultCurrent, pageSize: defaultPageSize }
    ],
    refreshDepsAction: () => {
      changeCurrent(1);
    },
    ...rest
  });
  const { current = 1, pageSize = defaultPageSize } = result.params[0] || {};
  const total = result.data?.total || 0;
  const totalPage = useMemo(
    () => Math.ceil(total / pageSize),
    [pageSize, total]
  );
  const onChange = (c, p) => {
    let toCurrent = c <= 0 ? 1 : c;
    const toPageSize = p <= 0 ? 1 : p;
    const tempTotalPage = Math.ceil(total / toPageSize);
    if (toCurrent > tempTotalPage) {
      toCurrent = Math.max(1, tempTotalPage);
    }
    const [oldPaginationParams = {}, ...restParams] = result.params || [];
    result.run(
      ...[
        {
          ...oldPaginationParams,
          current: toCurrent,
          pageSize: toPageSize
        },
        ...restParams
      ]
    );
  };
  const changeCurrent = (c) => {
    onChange(c, pageSize);
  };
  const changePageSize = (p) => {
    onChange(current, p);
  };
  return {
    ...result,
    pagination: {
      current,
      pageSize,
      total,
      totalPage,
      onChange: use_memoized_fn_default(onChange),
      changeCurrent: use_memoized_fn_default(changeCurrent),
      changePageSize: use_memoized_fn_default(changePageSize)
    }
  };
};
var use_pagination_default = usePagination;
var useWebSocket = (socketUrl, options = {}) => {
  const {
    reconnectLimit = 3,
    reconnectInterval = 3 * 1e3,
    manual = false,
    onOpen,
    onClose,
    onMessage,
    onError,
    protocols
  } = options;
  const [latestMessage, setLatestMessage] = useState();
  const [readyState, setReadyState] = useState(3 /* Closed */);
  const onOpenRef = use_latest_default(onOpen);
  const onCloseRef = use_latest_default(onClose);
  const onMessageRef = use_latest_default(onMessage);
  const onErrorRef = use_latest_default(onError);
  const readyStateRef = use_latest_default(readyState);
  const reconnectTimesRef = useRef(0);
  const reconnectTimerRef = useRef(void 0);
  const websocketRef = useRef(void 0);
  const reconnect = () => {
    if (reconnectTimesRef.current < reconnectLimit && websocketRef.current?.readyState !== 1 /* Open */) {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = setTimeout(() => {
        connectWs();
        reconnectTimesRef.current += 1;
      }, reconnectInterval);
    }
  };
  const connectWs = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
    if (websocketRef.current) {
      websocketRef.current.close();
    }
    const ws = new WebSocket(socketUrl, protocols);
    setReadyState(0 /* Connecting */);
    ws.onerror = (event) => {
      if (websocketRef.current !== ws) {
        return;
      }
      reconnect();
      onErrorRef.current?.(event, ws);
      setReadyState(ws.readyState || 3 /* Closed */);
    };
    ws.onopen = (event) => {
      if (websocketRef.current !== ws) {
        return;
      }
      onOpenRef.current?.(event, ws);
      reconnectTimesRef.current = 0;
      setReadyState(ws.readyState || 1 /* Open */);
    };
    ws.onmessage = (message) => {
      if (websocketRef.current !== ws) {
        return;
      }
      onMessageRef.current?.(message, ws);
      setLatestMessage(message);
    };
    ws.onclose = (event) => {
      onCloseRef.current?.(event, ws);
      if (websocketRef.current === ws) {
        reconnect();
      }
      if (!websocketRef.current || websocketRef.current === ws) {
        setReadyState(ws.readyState || 3 /* Closed */);
      }
    };
    websocketRef.current = ws;
  };
  const sendMessage = (message) => {
    if (readyStateRef.current === 1 /* Open */) {
      websocketRef.current?.send(message);
    } else {
      throw new Error("WebSocket disconnected");
    }
  };
  const connect = () => {
    reconnectTimesRef.current = 0;
    connectWs();
  };
  const disconnect = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
    reconnectTimesRef.current = reconnectLimit;
    websocketRef.current?.close();
    websocketRef.current = void 0;
  };
  useEffect(() => {
    if (!manual && socketUrl) {
      connect();
    }
  }, [socketUrl, manual]);
  use_unmount_default(() => {
    disconnect();
  });
  return {
    latestMessage,
    sendMessage: use_memoized_fn_default(sendMessage),
    connect: use_memoized_fn_default(connect),
    disconnect: use_memoized_fn_default(disconnect),
    readyState,
    webSocketIns: websocketRef.current
  };
};
var use_web_socket_default = useWebSocket;

export { clearCache, useAsync, useAsyncFn, useContinuousRetry, useFetch, useRequest_default, use_infinite_scroll_default, use_lock_fn_default, use_pagination_default, use_web_socket_default };
